/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Vehicle
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new vehicle in the organization.
 */

import { Formik, useFormik } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useCreateOrganizationVehicleMutation } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../../components/admin/formFields";
import { useLoggedInUserData } from "../../../utils/user";
import {
  formInitialValues,
  TFormFieldNames,
  YupValidationSchema,
} from "./validation";
import { useNavigate } from "react-router-dom";
import { routeUrls } from "../../../navigation/routeUrls";
import { getVehicleUUID } from "../../map-fleet/common";
import FTModal from "../../../components/common/FTModal";
import FormActionButtons from "../../../components/admin/formActionButtons";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";

const AdminsVehiclesCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.vehicles.create_new",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isbordercrossing = useLoggedInUserData("isbordercrossing");
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { getLocalStorageItem } = useLocalStorage();
  const enterpriseAccountData = getLocalStorageItem(
    localStorageKeys.ownerOrganization
  );

  // vehicle mutation
  const [createOrgVehicleAPITrigger] = useCreateOrganizationVehicleMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showCreateVehicle: false }));
  };
  const formik = useFormik({
    initialValues: formInitialValues,
    validationSchema: YupValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      // if(APP_CONFIG.TOASTS.INFO) toast.info(t('loginScreen.toast.logging_you_in'));
      createOrgVehicleAPITrigger({
        vehiclemake: values.make,
        vehiclemodel: values.model,
        // equipmenttype: values.vin, // equipmenttype is modified by device automatically
        vehicleid: values.vin, // vehicleid is modified manually
        licenseplate: values.license_plate,

        // required fields
        accountid: `${thisUserOrganizationId}`,
        cameraonoff: 0,
        deviceid: values.vehicle_id,
        lasteventstatuscode: 0,
        recordertype: 0, // enterpriseAccountData?.recordertype, // updated 10/9/2025 per client request now we will default to 'None' instead of account default recorder setting

        // we are intentionally setting the active flag to true
        isactive: values.isactive,
        bordercrossing: parseInt(isbordercrossing || "") === 1 ? 1 : 0,
      })
        .unwrap()
        .then((data) => {
          if (!!data?.deviceid) {
            dispatch(
              setModalsData({ ...modalsState, showCreateVehicle: false })
            );
            toast.success(t("toast.create_success"), { autoClose: 10000 });
            navigate(
              `${routeUrls.dashboardChildren.adminChildren.vehicles}/${getVehicleUUID(data)}`,
              { state: { new: true } }
            );
          } else {
            toast.error(t("toast.create_failed"));
          }
        })
        .catch((error) => {
          // disabled the error popup here as another popup shows via `errorCodes.ts`
          // and it was showing duplicate error popups
          // so commenting out this code for now
          // ---------------------------------------------
          // const errors = !!error?.data
          //   ? serializeErrorKeyValues(error?.data)
          //   : [t("toast.create_failed")];
          // toast.error(errors?.join(" "));
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
  } = formik;
  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: t("submit_button"),
              variant: "primary",
              disabled: !isFormValid || isSubmitting /* || isLoading*/,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled: isSubmitting /* || isLoading*/,
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, [isFormValid, isSubmitting]);
  return (
    <FTModal
      visible={modalsState.showCreateVehicle}
      heading={t("heading")}
      onHide={hideModal}
    >
      <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
        <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
          {t("sub_heading")}
        </p>

        <AdminFormFieldInput
          label={t("vehicle_id")}
          type="text"
          id="VehicleId"
          name="vehicle_id"
          placeholder={t("vehicle_id_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.vehicle_id}
          touched={touched.vehicle_id}
          error={errors.vehicle_id}
        />

        <AdminFormFieldInput
          label={t("make")}
          type="text"
          id="Make"
          name="make"
          placeholder={t("make_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.make}
          touched={touched.make}
          error={errors.make}
        />

        <AdminFormFieldInput
          label={t("model")}
          type="text"
          id="Model"
          name="model"
          placeholder={t("model_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.model}
          touched={touched.model}
          error={errors.model}
        />

        <AdminFormFieldInput
          label={t("vin")}
          type="text"
          id="Vin"
          name="vin"
          placeholder={t("vin_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.vin}
          touched={touched.vin}
          error={errors.vin}
        />

        <AdminFormFieldInput
          label={t("license_plate")}
          type="text"
          id="LicensePlate"
          name="license_plate"
          placeholder={t("license_plate_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.license_plate}
          touched={touched.license_plate}
          error={errors.license_plate}
        />
        {MemoizedFormActionButtons}
        {/* <AdminFormFieldSubmit
              label={t('submit_button')}
              variant="primary"
              disabled={!isFormValid || isSubmitting }// || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting} // || isLoading
              onClick={hideModal}
            /> */}
      </form>
    </FTModal>
  );
};
export default AdminsVehiclesCreateNew;
